/**
 * Scoping a logged-in session to the marketplace of the current domain (#1397).
 *
 * GET /user/account/getByJWT returns every account the user owns or collaborates on, across
 * all marketplaces. A user who belongs to two marketplaces (e.g. Villa Accadia and Kaete Club,
 * both Dan Hotels) got a dashboard that mixed them: on members.kaeteclub.com the calendar,
 * stats and sub-accounts were Villa's. On a white-label domain the session must only see the
 * accounts of that domain's marketplace.
 *
 * Kept free of imports so it runs under node:test.
 */

const marketplaceIdOf = (account) => {
    const { marketPlace } = account || {};
    if (!marketPlace) return null;
    return String(typeof marketPlace === 'object' ? marketPlace._id : marketPlace);
};

const hostOf = (url) => {
    try {
        return new URL(url).hostname.toLowerCase();
    } catch {
        return null;
    }
};

// Same host, or one is a subdomain of the other: members.kaeteclub.com vs the market.js key
// kaeteclub.com (what ?domain= previews on localhost), www.bambooclub.co.il vs bambooclub.co.il.
const hostsMatch = (a, b) => a === b || a.endsWith(`.${b}`) || b.endsWith(`.${a}`);

/**
 * @param {Array<object>} accounts - getByJWT accounts; `marketPlace` is populated with `baseAppURL`
 *   (or not, before the backend ships it — then nothing is scoped)
 * @param {string|null} hostname - the current marketplace hostname, or null on a non white-label
 *   host (nimi.co.il serves sellers of many marketplaces, so it must never narrow the session)
 * @returns {{ accounts: Array<object>, marketplaceId: string|null }} the accounts to use, and the
 *   marketplace they were narrowed to (null when left as is)
 */
export const scopeAccountsToHost = (accounts, hostname) => {
    const unscoped = { accounts, marketplaceId: null };
    if (!Array.isArray(accounts) || !hostname) return unscoped;

    const host = hostname.toLowerCase();
    const marketplaceIds = new Set(accounts.map(marketplaceIdOf).filter(Boolean));
    // A single-marketplace user — almost everyone — is never touched.
    if (marketplaceIds.size < 2) return unscoped;

    const matching = new Set();
    for (const account of accounts) {
        const baseAppHost = hostOf(account?.marketPlace?.baseAppURL);
        if (baseAppHost && hostsMatch(baseAppHost, host)) matching.add(marketplaceIdOf(account));
    }
    // No match: the user has no account in this domain's marketplace, so keep today's behavior.
    // Several: ambiguous, so don't guess.
    if (matching.size !== 1) return unscoped;

    const [marketplaceId] = matching;
    return {
        accounts: accounts.filter((account) => marketplaceIdOf(account) === marketplaceId),
        marketplaceId,
    };
};

// Second-level public suffixes in use (Israeli .co.il / .org.il …, .co.uk). Enough for the
// domains the platform serves; anything else falls back to the last two labels.
const TWO_LABEL_SUFFIXES = ['co.il', 'org.il', 'ac.il', 'net.il', 'gov.il', 'muni.il', 'co.uk', 'org.uk'];

/** members.kaeteclub.com → kaeteclub.com, crm.radical.org.il → radical.org.il */
export const registrableDomain = (host) => {
    const labels = host.toLowerCase().split('.').filter(Boolean);
    const lastTwo = labels.slice(-2).join('.');
    return labels.slice(TWO_LABEL_SUFFIXES.includes(lastTwo) ? -3 : -2).join('.');
};

/**
 * A white-label domain must not render another marketplace's seller page (#1407):
 * members.kaeteclub.com/seller/villa showed Villa Accadia's member page under Kaete Club's header,
 * and a sign-in there ran against Villa. Returns the URL of the same page on the seller's own
 * domain when the current host belongs to a different marketplace, else null.
 *
 * @param {object} params
 * @param {string|undefined} params.baseAppURL - the seller's marketPlace.baseAppURL (absent on a
 *   backend that predates nimi-backend#1254 — then nothing is redirected)
 * @param {string|null} params.hostname - the current white-label hostname, or null on a generic
 *   host (nimi.co.il serves every marketplace's sellers)
 * @param {string} [params.pathname]
 * @param {string} [params.search]
 */
export const foreignMarketplaceUrl = ({ baseAppURL, hostname, pathname = '/', search = '' }) => {
    if (!hostname || !baseAppURL) return null;
    let target;
    try {
        target = new URL(baseAppURL);
    } catch {
        return null;
    }
    // Same registrable domain = same site: a brand may serve its sellers on sibling subdomains
    // (radical's baseAppURL is crm.radical.org.il while *.radical.org.il serves it).
    if (registrableDomain(target.hostname) === registrableDomain(hostname)) return null;
    // ?domain= is a localhost/nimi.co.il preview switch; it means nothing on the target site
    const params = new URLSearchParams(search);
    params.delete('domain');
    const query = params.toString();
    return `${target.origin}${pathname}${query ? `?${query}` : ''}`;
};
